import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as XLSX from 'xlsx';
import {
	Typography,
	Button,
	Container,
	Paper,
	Grid,
	Snackbar,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	List,
	ListItem,
	LinearProgress,
	ListItemIcon,
	ListItemText,
	CircularProgress,
	Box,
	TextField,
	Alert,
	Slider,
	Stepper,
	Step,
	StepLabel,
} from '@mui/material';
import {
	WhatsApp as WhatsAppIcon,
	CloudUpload as CloudUploadIcon,
	Phone as PhoneIcon,
	Download as DownloadIcon,
	Refresh as RefreshIcon,
	PhoneDisabled as PhoneDisabledIcon,
	Settings as SettingsIcon,
	TableChart as TableChartIcon,
	PlayArrow as PlayArrowIcon,
	CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { toWapi } from '../../util/api';

function WhatsAppMultiChecker(props) {
	const [activeStep, setActiveStep] = useState(0);
	const [selectedPhone, setSelectedPhone] = useState('');
	const [selectBoxOptions, setSelectBoxOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState('success');
	const [numbers, setNumbers] = useState([]);
	const [results, setResults] = useState([]);
	const [fileName, setFileName] = useState('');
	const [numbersPerCheck, setNumbersPerCheck] = useState(150);
	const [intervalMinutes, setIntervalMinutes] = useState(20);
	const [isDragging, setIsDragging] = useState(false);
	const [hasValidNumbers, setHasValidNumbers] = useState(false);
	const [countdown, setCountdown] = useState(0);
	const [isCountingDown, setIsCountingDown] = useState(false);
	
	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDragIn = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragOut = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};
	const formatCountdown = (seconds) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
	};
	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			const file = files[0];
			if (
				file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				file.type === 'application/vnd.ms-excel' ||
				file.type === 'text/csv'
			) {
				handleFileUpload({ target: { files: [file] } });
			} else {
				showSnackbar('Please upload only Excel files (.xlsx, .xls, .csv)', 'error');
			}
		}
	};

	useEffect(() => {
		fetchPhoneList();
	}, [props.product]);

	// Example Number ?
	const excelPhoneNumbers = [
		'11234567890',
		'441234567890',
		'10123456789',
		'440123456789',
		'610123456789',
		'91123456789',
		'905321234567',
		'391234567890',
		'911234567890',
		'521234567890',
	];

	// Create Example Excel File
	const handleExportExcel = () => {
		const data = excelPhoneNumbers.map((number) => [number]);
		const ws = XLSX.utils.aoa_to_sheet(data);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'phones');
		XLSX.writeFile(wb, 'ExamplePhoneNumbers.xlsx');
	};

	const fetchPhoneList = async () => {
		const { product } = props;
		if (product?.id && product?.settings?.wapi_token) {
			const {
				id,
				settings: { wapi_token },
			} = product;

			try {
				const data = await toWapi(`/api/${id}/listPhones`, wapi_token);
				if (data?.status === 200 && Array.isArray(data.data)) {
					const activePhones = data.data.filter((phone) => phone.status === 'active');
					setSelectBoxOptions(activePhones);
				}
			} catch (err) {
				console.error('Error fetching phone list:', err);
				showSnackbar('Error fetching phone list', 'error');
			}
		}
	};

	const handleFileUpload = (event) => {
		const file = event.target.files[0];
		if (!file) {
			console.log('No file selected');
			return;
		}

		setFileName(file.name);
		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target.result);
				const workbook = XLSX.read(data, { type: 'array' });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];

				const rawData = XLSX.utils.sheet_to_json(worksheet, {
					header: 1,
					defval: '',
					blankrows: false,
				});

				const phoneNumbers = rawData.map((row) => String(row[0] || '').replace(/[^0-9]/g, '')).filter((number) => number && number.length > 0);

				if (phoneNumbers.length === 0) {
					setHasValidNumbers(false);
					showSnackbar('No valid phone numbers found in the Excel file. Please check the format.', 'error');
					setNumbers([]);
					return;
				}
				setHasValidNumbers(true);
				setNumbers(phoneNumbers);
				setNumbersPerCheck(Math.ceil(phoneNumbers.length / 4));
				setResults([]);
				showSnackbar(`${phoneNumbers.length} numbers loaded successfully`, 'success');
			} catch (error) {
				setHasValidNumbers(false);
				console.error('Error processing Excel file:', error);
				showSnackbar(`Error processing Excel file: ${error.message}`, 'error');
			}
		};

		reader.onerror = (error) => {
			console.error('FileReader error:', error);
			showSnackbar('Error reading the file', 'error');
		};

		try {
			reader.readAsArrayBuffer(file);
		} catch (error) {
			setHasValidNumbers(false);
			console.error('Error starting file read:', error);
			showSnackbar('Error reading the file', 'error');
		}
	};

	const checkNumber = async (number) => {
		const { product } = props;
		const {
			id,
			settings: { wapi_token },
		} = product;

		try {
			const response = await toWapi(`/api/${id}/${selectedPhone}/checknumberstatus?number=${number}@c.us`, wapi_token);

			return {
				number,
				hasWhatsApp: response?.data?.success === true && response?.data?.result?.status === 200,
				status: response?.data?.result?.status || 'error',
			};
		} catch (error) {
			console.error(`Error checking number ${number}:`, error);
			return {
				number,
				hasWhatsApp: false,
				status: 'error',
			};
		}
	};

	const processNumbers = async () => {
		if (!selectedPhone || numbers.length === 0) {
			showSnackbar('Please select a phone and upload numbers', 'error');
			return;
		}
		setResults([]);
		const newResults = [];
		try {
			for (let i = 0; i < numbers.length; i++) {
				if (i > 0 && i % numbersPerCheck === 0) {
					setIsCountingDown(true);
					setCountdown(intervalMinutes * 60);
					
					// Alert Countdown
					const countdownInterval = setInterval(() => {
						setCountdown(prev => {
							if (prev <= 1) {
								clearInterval(countdownInterval);
								setIsCountingDown(false);
								return 0;
							}
							return prev - 1;
						});
					}, 1000);
	
					await new Promise((resolve) => setTimeout(resolve, intervalMinutes * 60 * 1000));
				}
				const result = await checkNumber(numbers[i]);
				newResults.push(result);
				setResults([...newResults]);
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
			showSnackbar('All numbers checked successfully', 'success');
		} catch (error) {
			console.error('Error processing numbers:', error);
			showSnackbar('Error occurred while checking numbers', 'error');
		}
	};
	const showSnackbar = (message, severity) => {
		setSnackbarMessage(message);
		setSnackbarSeverity(severity);
		setOpenSnackbar(true);
	};
	const handleCloseSnackbar = () => {
		setOpenSnackbar(false);
	};
	const downloadResults = () => {
		try {
			if (results.length === 0) {
				showSnackbar('No results to download', 'error');
				return;
			}
			setIsLoading(true);
			// Download One File Excel
			const processedData = [
				/*['Phone Number', 'WhatsApp Status'],*/ ...results.map((result) => [result.number, result.hasWhatsApp ? 'Valid' : 'Invalid']),
			];
			const ws = XLSX.utils.aoa_to_sheet(processedData);
			const colWidths = {
				A: { wch: 15 },
				B: { wch: 12 },
			};
			ws['!cols'] = [colWidths.A, colWidths.B];
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, 'WhatsApp Check Results');
			let exportFileName = 'whatsapp_check_results.xlsx';
			if (fileName) {
				exportFileName = fileName.includes('.xlsx') ? fileName.replace('.xlsx', '_results.xlsx') : fileName.replace('.xls', '_results.xlsx');
			}
			XLSX.writeFile(wb, exportFileName, { bookType: 'xlsx' });
			showSnackbar('Results downloaded successfully', 'success');
		} catch (error) {
			console.error('Download error:', error);
			showSnackbar('Error downloading results: ' + error.message, 'error');
		} finally {
			setIsLoading(false);
		}
	};
	const handleNext = () => {
		if (activeStep === 0 && (!selectedPhone || numbers.length === 0)) {
			showSnackbar('Please select a phone and upload numbers', 'error');
			return;
		}
		setActiveStep((prevStep) => prevStep + 1);
		if (activeStep === 0) {
			processNumbers();
		}
	};
	const steps = [
		{
			label: 'Upload and Configure',
		},
		{
			label: 'Process and Results',
		},
	];
	return (
		<Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
			<Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 3 }}>
				WhatsApp Multi Number Checker
			</Typography>
			{activeStep === 0 && (
				<Typography sx={{ mb: 2 }}>
					Multiple WhatsApp Number Checker is an easy-to-use tool designed to quickly and effortlessly check if multiple phone numbers are registered
					in WhatsApp. To use it, simply select one of your active phone numbers, upload the file with the numbers you want to check to the system,
					make the settings that are most suitable for you and click the 'Check numbers' button. The tool will immediately process your request and
					provide you with the result, which you can use for various purposes. From the result screen you will be able to see the phone numbers that
					use WhatsApp and those that do not. This versatile checker provides an easy way to verify WhatsApp numbers.
				</Typography>
			)}
			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Stepper activeStep={activeStep} sx={{ mb: 4 }}>
					{steps.map((step) => (
						<Step key={step.label}>
							<StepLabel>
								<Typography variant="subtitle1">{step.label}</Typography>
							</StepLabel>
						</Step>
					))}
				</Stepper>

				{activeStep === 0 ? (
					<Grid container spacing={3}>
						<Grid item xs={12} md={7}>
							<FormControl fullWidth sx={{ mb: 2 }}>
								<InputLabel>Select Active Phone</InputLabel>
								<Select value={selectedPhone} onChange={(e) => setSelectedPhone(e.target.value)} label="Select Active Phone">
									{selectBoxOptions.map((phone) => (
										<MenuItem key={phone.id} value={phone.id}>
											{`${phone.id} - ${phone.number}${phone.name ? ` - ${phone.name}` : ''}`}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<Box
								onDragEnter={handleDragIn}
								onDragLeave={handleDragOut}
								onDragOver={handleDrag}
								onDrop={handleDrop}
								sx={{
									border: '2px dashed',
									borderColor: isDragging ? 'primary.main' : 'grey.300',
									borderRadius: 1,
									p: 1,
									mb: 2,
									cursor: 'pointer',
									transition: 'all 0.2s ease',
									'&:hover': {
										borderColor: 'primary.main',
										bgcolor: 'rgba(0, 0, 0, 0.04)',
									},
								}}
							>
								<Button variant="contained" component="label" fullWidth startIcon={<CloudUploadIcon />} sx={{ mb: isDragging ? 1 : 0 }}>
									Upload Excel File (.xlsx, .xls, .csv)
									<input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleFileUpload} onClick={(e) => (e.target.value = null)} />
								</Button>
								{isDragging ? (
									<Typography align="center" color="primary">
										Drop the file here...
									</Typography>
								) : (
									<Typography align="center" variant="body2" color="text.secondary">
										or drag and drop file here
									</Typography>
								)}
								{fileName && (
									<Typography align="center" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
										Selected file: {fileName}
									</Typography>
								)}
							</Box>

							<Button
								variant="contained"
								fullWidth
								onClick={handleNext}
								startIcon={<PlayArrowIcon />}
								disabled={!selectedPhone || !hasValidNumbers || numbers.length === 0}
								sx={{ mb: 2 }}
							>
								Check Numbers
							</Button>

							{numbers.length > 0 && (
								<>
									<Box sx={{ mt: 2, mb: 2 }}>
										<Alert severity="info">
											<Typography variant="body2">
												Loaded {numbers.length} numbers from: {fileName}
											</Typography>
											<Typography variant="caption" display="block">
												First few numbers: {numbers.slice(0, 3).join(', ')}
												{numbers.length > 3 ? '...' : ''}
											</Typography>
										</Alert>
									</Box>

									<Paper elevation={3} sx={{ p: 3, mt: 2, mb: 2 }}>
										<Typography variant="h6" gutterBottom>
											Setting up process speed
										</Typography>

										<Box sx={{ width: '100%', mb: 3 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
												<Typography id="numbers-per-check" gutterBottom>
													Number to be checked at one time:
												</Typography>
												<TextField
													type="number"
													value={numbersPerCheck}
													onChange={(e) => {
														const value = parseInt(e.target.value);
														if (value > 0) {
															setNumbersPerCheck(Math.min(value, numbers.length));
														}
													}}
													size="small"
													sx={{ ml: 2, width: '100px' }}
													InputProps={{
														inputProps: {
															min: 1,
															max: numbers.length, 
														},
													}}
												/>
											</Box>
											<Slider
												value={numbersPerCheck}
												onChange={(e, newValue) => setNumbersPerCheck(Math.min(newValue, numbers.length))}
												aria-labelledby="numbers-per-check"
												valueLabelDisplay="auto"
												step={1}
												marks
												min={1}
												max={numbers.length} 
											/>
										</Box>

										<Box sx={{ width: '100%' }}>
											<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
												<Typography id="interval-minutes" gutterBottom>
													Interval between checks:
												</Typography>

												<TextField
													type="number"
													value={intervalMinutes}
													onChange={(e) => {
														const value = parseInt(e.target.value) || 5; 

														if (value >= 1 && value <= 120) {
															setIntervalMinutes(value);
														} else if (value < 1) {
															setIntervalMinutes(1);
														} else if (value > 120) {
															setIntervalMinutes(120);
														}
													}}
													size="small"
													sx={{ ml: 2, width: '100px' }}
													InputProps={{
														inputProps: {
															min: 1,
															max: 120,
														},
													}}
												/>
												<Typography sx={{ ml: 1 }}>minutes</Typography>
											</Box>
											<Slider
												value={intervalMinutes}
												onChange={(e, newValue) => setIntervalMinutes(newValue)}
												aria-labelledby="interval-minutes"
												valueLabelDisplay="auto"
												step={1}
												marks
												min={1}
												max={120}
											/>
										</Box>

										<Box sx={{ mt: 3 }}>
											<Typography variant="body1">
												You will check <strong>{numbersPerCheck}</strong> numbers at a time with a <strong>{intervalMinutes}</strong>{' '}
												minute interval between checks.
											</Typography>
										</Box>
									</Paper>
								</>
							)}
						</Grid>

						<Grid item xs={12} md={5}>
							<Paper elevation={2}>
								<Typography sx={{ ml: 2, mt: 2 }} variant="h6" gutterBottom>
									Example Checker Document Format
								</Typography>
								<Typography sx={{ ml: 2, mt: 2 }}>
									Use this sample file to understand the correct format and where to enter the numbers.
								</Typography>
								<Button
									component="label"
									startIcon={<DownloadIcon />}
									sx={{
										width: '93%',
										ml: 2,
										marginBottom: '10px',
										marginTop: '10px',
										backgroundColor: '#1D6F42',
										color: 'white',
										'&:hover': {
											backgroundColor: '#145B2F',
										},
									}}
									variant="contained"
									color="primary"
									onClick={handleExportExcel}
								>
									Example Number File for Checker
								</Button>
								<Typography sx={{ ml: 2 }} variant="h6" gutterBottom>
									Instructions
								</Typography>
								<List>
									<ListItem>
										<ListItemIcon>
											<PhoneIcon color="primary" />
										</ListItemIcon>
										<ListItemText primary="Select your active WhatsApp phone number" />
									</ListItem>
									<ListItem>
										<ListItemIcon>
											<TableChartIcon color="primary" />
										</ListItemIcon>
										<ListItemText
											primary="Upload Excel file with phone numbers"
											secondary="Numbers should be in column A, starting from A1"
										/>
									</ListItem>
									<ListItem>
										<ListItemIcon>
											<SettingsIcon color="primary" />
										</ListItemIcon>
										<ListItemText primary="Configure check settings and continue" />
									</ListItem>
									<ListItem>
										<ListItemIcon>
											<PlayArrowIcon color="primary" />
										</ListItemIcon>
										<ListItemText primary="Click Check Numbers to start processing" />
									</ListItem>
								</List>
							</Paper>
						</Grid>
					</Grid>
				) : (
					<Box sx={{ mt: 2 }}>
						   {isCountingDown && (
            <Alert 
                severity="info" 
                sx={{ 
                    mb: 3,
                    '& .MuiAlert-message': {
                        width: '100%'
                    }
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
				Waiting for next batch please do not close the page
                </Typography>
                <Typography sx={{ mb: 1 }}>
                    Time remaining: {formatCountdown(countdown)}
                </Typography>
                <LinearProgress 
                    variant="determinate" 
                    value={(countdown / (intervalMinutes * 60)) * 100}
                    sx={{ 
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 5,
                            backgroundColor: '#1976d2'
                        }
                    }}
                />
            </Alert>
        )}
						{results.length > 0 && (
							<Box sx={{ mt: 3 }}>
								<Box sx={{ textAlign: 'center', mb: 4 }}>
									{/* Circular Progress Area */}
									<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
										<Box
											sx={{
												position: 'relative',
												display: 'inline-flex',
												width: '120px',
												height: '120px',
											}}
										>
											<CircularProgress
												variant="determinate"
												value={(results.length / numbers.length) * 100}
												size={120}
												thickness={4}
												sx={{
													color: '#25D366',
													position: 'absolute',
													left: 0,
												}}
											/>
											<Box
												sx={{
													top: 0,
													left: 0,
													bottom: 0,
													right: 0,
													position: 'absolute',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
												}}
											>
												<Typography variant="h4" component="div" color="text.secondary">
													{`${results.length}/${numbers.length}`}
												</Typography>
											</Box>
										</Box>
									</Box>
									<Typography variant="h5" sx={{ mt: 2, color: '#1a1a1a' }}>
										Numbers check completed
									</Typography>
								</Box>

								<Grid container spacing={4} sx={{ mb: 4 }}>
									<Grid item xs={12} md={6}>
										<Paper
											elevation={3}
											sx={{
												p: 3,
												textAlign: 'center',
												border: '2px solid #25D366',
											}}
										>
											<WhatsAppIcon sx={{ fontSize: 40, color: '#25D366', mb: 2 }} />
											<Typography variant="h6">Valid Numbers</Typography>
											<Typography variant="h4" sx={{ color: '#25D366', my: 2 }}>
												{results.filter((r) => r.hasWhatsApp).length}/{results.length}
											</Typography>
											<Button
												variant="contained"
												startIcon={<CloudDownloadIcon />}
												onClick={() => {
													const validNumbers = results.filter((r) => r.hasWhatsApp).map((result) => [result.number]);
													const ws = XLSX.utils.aoa_to_sheet(validNumbers);
													const colWidths = {
														A: { wch: 15 },
													};
													ws['!cols'] = [colWidths.A];
													const wb = XLSX.utils.book_new();
													XLSX.utils.book_append_sheet(wb, ws, 'Valid WhatsApp Numbers');
													XLSX.writeFile(wb, 'valid_whatsapp_numbers.xlsx');
												}}
												sx={{
													backgroundColor: '#25D366',
													'&:hover': { backgroundColor: '#128C7E' },
												}}
											>
												Download result
											</Button>
										</Paper>
									</Grid>

									<Grid item xs={12} md={6}>
										<Paper
											elevation={3}
											sx={{
												p: 3,
												textAlign: 'center',
												border: '2px solid gray',
											}}
										>
											<PhoneDisabledIcon sx={{ fontSize: 40, color: 'gray', mb: 2 }} />
											<Typography variant="h6">Not Valid Numbers</Typography>
											<Typography variant="h4" sx={{ color: 'gray', my: 2 }}>
												{results.filter((r) => !r.hasWhatsApp).length}/{results.length}
											</Typography>
											<Button
												variant="contained"
												startIcon={<CloudDownloadIcon />}
												onClick={() => {
													const invalidNumbers = results.filter((r) => !r.hasWhatsApp).map((result) => [result.number]);
													const ws = XLSX.utils.aoa_to_sheet(invalidNumbers);
													const colWidths = {
														A: { wch: 15 },
													};
													ws['!cols'] = [colWidths.A];
													const wb = XLSX.utils.book_new();
													XLSX.utils.book_append_sheet(wb, ws, 'Invalid WhatsApp Numbers');
													XLSX.writeFile(wb, 'invalid_whatsapp_numbers.xlsx');
												}}
												sx={{ backgroundColor: 'gray' }}
											>
												Download result
											</Button>
										</Paper>
									</Grid>
								</Grid>
								
								<Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
									<Button
										fullWidth
										variant="contained"
										startIcon={<CloudDownloadIcon />}
										onClick={downloadResults}
										disabled={isLoading || results.length === 0}
									>
										{isLoading ? (
											<>
												<CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
												Downloading...
											</>
										) : (
											'Download All Results'
										)}
									</Button>
									<Button
										fullWidth
										variant="outlined"
										onClick={() => {
											window.location.reload();
										}}
										startIcon={<RefreshIcon />}
									>
										Run a new check
									</Button>
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Paper>

			<Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
				<Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</Container>
	);
}

const stateToProps = (state) => ({
	product: state.product.maytapi,
});

export default connect(stateToProps)(WhatsAppMultiChecker);